import { SearchTrackerService } from '@app/search/services';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { SearchFilterFilter } from '@app/search/search-api.model';
import { SearchFilterId } from '@app/search/services/search-filters.service';
import { TranslateService } from '@ngx-translate/core';
import { SearchFiltersStateService } from '../search-filters/search-filters-state.service';
import { getSelectedFilterCount } from '../search-view/search-reactive-store/utils';

@Component({
  selector: 'dgx-search-filters-sidebar',
  templateUrl: './search-filters-sidebar.component.html',
  styleUrls: ['./search-filters-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFiltersSidebarComponent implements AfterViewChecked {
  @Input() public filters: SearchFilterFilter[] = [];
  @Input() public isModal: boolean = false;
  @Input() public showFilterReset: boolean;

  @Output() public filtersChange = new EventEmitter<SearchFilterFilter[]>();
  @Output() public resetFilters = new EventEmitter<null>();

  @ViewChild('wrapper')
  private wrapper: ElementRef<HTMLElement>;
  @ViewChild('resetButton')
  private resetButton: ElementRef<HTMLElement>;

  private _isLoading = true;

  // we need to set isLoading to false when the parent component
  // is done loading and never set it back to true so the sidebar
  // stays visible for subsequent filter/search/sort requests
  @Input() public set isLoading(value: boolean) {
    if (!value) {
      this._isLoading = false;
    }
  }
  public get isLoading(): boolean {
    return this._isLoading;
  }

  // Switching to/from marketplace clears the filters temporarily
  // so we show the skeleton until they are reset
  public get showLoadingSkeleton() {
    return this.isLoading || this.filters?.length === 0;
  }

  private _fixedSidebar: ElementRef;

  @ViewChild('fixedSidebar', { static: false }) set sidebar(
    sidebar: ElementRef
  ) {
    // Don't try to set up sidebar fixed position until it's available on the DOM
    if (sidebar) {
      this._fixedSidebar = sidebar;
      if (!this.isModal) {
        this.cdr.markForCheck();
        this.setSidebarPosition();
        this.buildResizeObserver();
      }
    }
  }

  private expandedFilters = new Set<SearchFilterId>().add(SearchFilterId.Type);

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private searchFiltersStateService: SearchFiltersStateService,
    private searchTrackerService: SearchTrackerService
  ) {}

  public get resetFiltersText(): string {
    const count = getSelectedFilterCount(this.filters);

    return this.translateService.instant('Core_ResetFiltersWithCount', {
      count,
    });
  }

  @HostListener('window:scroll')
  public onScroll() {
    // 1 - we don't need a fixed sidebar on the modal
    // 2 - we'll get type errors if the user scrolls before the DOM has settled (i.e. loading skeletons)
    if (this.isModal || !this.wrapper) {
      return;
    }

    const { top } = this.wrapper.nativeElement.getClientRects()[0];

    // Freeze the sidebar under the header
    this._fixedSidebar.nativeElement.style.top =
      top < 95 ? '7.5rem' : `${top}px`;
  }

  public ngAfterViewChecked(): void {
    const sidebarWidth = this.wrapper?.nativeElement.clientWidth;
    const resetButtonWidth = this.resetButton?.nativeElement.clientWidth;

    // Make sure the reset button width matches the sidebar width
    if (
      !!this.resetButton &&
      !this.isLoading &&
      sidebarWidth !== resetButtonWidth
    ) {
      this.resetButton.nativeElement.style.width = `${sidebarWidth}px`;
    }

    this.setSidebarPosition();
  }

  // don't render any filters that will be rendered as a part of a range filter - eg Price Range  - or that have no
  // subitems to select
  public showFilter(filter: SearchFilterFilter): boolean {
    return !!filter.subitems.length;
  }

  public onToggle(id: SearchFilterId) {
    const isExpanded = this.expandedFilters.has(id);

    isExpanded ? this.expandedFilters.delete(id) : this.expandedFilters.add(id);

    this.searchTrackerService.searchResultViewChangeInitiated(id);
  }

  public isFilterExpanded(filter: SearchFilterFilter): boolean {
    return this.expandedFilters.has(filter.id as SearchFilterId);
  }

  public filterChange(updatedFilter: SearchFilterFilter) {
    if (updatedFilter.id === SearchFilterId.Language) {
      this.searchFiltersStateService.setIgnorePreferredLanguage(true);
    }

    const filters = this.filters.map((filter) =>
      updatedFilter.id === filter.id ? updatedFilter : filter
    );

    this.filtersChange.emit(filters);
  }

  public onFilterReset() {
    this.expandedFilters.clear();
    this.expandedFilters.add(SearchFilterId.Type);
    this.resetFilters.emit();
  }

  // Keep the fixed sidebar width in sync with the wrapper
  private buildResizeObserver() {
    const wrapper = this.wrapper.nativeElement;
    const observer = new ResizeObserver((entries) =>
      entries.forEach(this.matchWidthOnResize.bind(this))
    );

    observer.observe(wrapper);
  }

  private matchWidthOnResize({ contentRect: { width } }: ResizeObserverEntry) {
    // Match the `fixed` sidebar width to it's wrapper
    this._fixedSidebar.nativeElement.style.width = `${width}px`;

    // Same for the filter reset button
    if (this.resetButton) {
      this.resetButton.nativeElement.style.width = `${width}px`;
    }
  }

  private setSidebarPosition() {
    const sidebar = this._fixedSidebar?.nativeElement;

    if (sidebar) {
      const { top, width } = this.wrapper.nativeElement.getClientRects()[0];
      // set sidebar to same `top` & `width` as the wrapper so it stays in place when `fixed`
      sidebar.style.top = `${top}px`;
      sidebar.style.width = `${width}px`;

      // set `bottom` so scrolling works properly
      sidebar.style.bottom = `4.5rem`;

      sidebar.style.position = `fixed`;
    }
  }
}
